import { formatDateToYYYYMMDD } from "../utils/DateParser";
import { extractTopLevelBracketExpressions } from "../utils/Strings";
import { objectToString, parseStringToObject } from "../utils/Objects";
import { convertGenderValuesToNames, AgeMappings, GENDER_NAMES, GENDER_VALUES } from "./utils";

const filterVersion = "v1";
const groupFilterRegex = /\)&\(|\)\|\(/;
const operators = new Map([
    ["&", "and"],
    ["|", "or"],
]);

export class SegmentFilter {
    filter: Filters;
    stringFilter = "";

    constructor(
        filter: Filters = {
            version: filterVersion,
            filters_by_ids: { operands: [] },
            filters_for_db: { operands: [] },
        }
    ) {
        this.filter = filter;
        this.stringFilter = this.convertFilterToString();
    }

    findDateOperands(filter: Filter = this.filter.filters_by_ids): Operand[] {
        const dateOperands: Operand[] = [];

        if (filter.operands && Array.isArray(filter.operands)) {
            for (const operand of filter.operands) {
                if ("type" in operand && operand.type === "date") {
                    dateOperands.push(operand);
                } else if ("operands" in operand) {
                    dateOperands.push(...this.findDateOperands(operand));
                }
            }
        }
        return dateOperands;
    }

    findGenderOperands(filter: Filter = this.filter.filters_by_ids): Operand[] {
        const genderOperands: Operand[] = [];

        if (filter.operands && Array.isArray(filter.operands)) {
            for (const operand of filter.operands) {
                if ("type" in operand && operand.type === "gender") {
                    genderOperands.push(operand);
                } else if ("operands" in operand) {
                    genderOperands.push(...this.findGenderOperands(operand));
                }
            }
        }
        return genderOperands;
    }

    findAgeOperands(filter: Filter = this.filter.filters_by_ids): Operand[] {
        const ageOperands: Operand[] = [];

        if (filter.operands && Array.isArray(filter.operands)) {
            for (const operand of filter.operands) {
                if ("type" in operand && operand.type === "age") {
                    ageOperands.push(operand);
                } else if ("operands" in operand) {
                    ageOperands.push(...this.findAgeOperands(operand));
                }
            }
        }
        return ageOperands;
    }

    setFilterByStr(str: string) {
        this.filter.filters_by_ids = this.convertStringToFilter(str);
    }

    setFilterBySegment(segment: Segment, ageMappings?: AgeMappings) {
        let filter: Filter;
        let filterForDB: Filter;

        if (segment.conditionsGroups.length > 1) {
            filter = { operator: "or", operands: [] };
            filterForDB = { operator: "or", operands: [] };
        }

        segment.conditionsGroups.forEach((group) => {
            const groupFilter = this.createBaseGroupFilter(group);
            const groupFilterForDB = JSON.parse(JSON.stringify(groupFilter));

            this.addCategoryAndCatalogData(groupFilter, groupFilterForDB, group, ageMappings);

            if (segment.conditionsGroups.length > 1) {
                filter.operands.push(groupFilter);
                filterForDB.operands.push(groupFilterForDB);
            } else {
                filter = { ...groupFilter };
                filterForDB = { ...groupFilterForDB };
            }

            this.updateFilters(filter, filterForDB);
        });

        const group = segment.conditionsGroups[0];
        const minCount = group?.minPurchasesCount;
        const maxCount = group?.maxPurchasesCount;
        this.filter.min_purchases_count =
            minCount != null && Number.isInteger(minCount) && minCount >= 2
                ? minCount
                : null;
        this.filter.max_purchases_count =
            maxCount != null && Number.isInteger(maxCount) && maxCount >= 2
                ? maxCount
                : null;
    }

    createBaseGroupFilter(group: ConditionsGroup): Filter {
        const groupFilter: Filter = {
            operator: "and",
            operands: [],
        };

        if (group.period.start && group.isFixedDate) {
            this.addPeriodToFilter(groupFilter, group.period.start, "gte");
        }

        if (group.period.end && group.isFixedDate) {
            this.addPeriodToFilter(groupFilter, group.period.end, "lte");
        }

        if (group.flexible_period.start.interval > 0 && group.isFlexibleDate) {
            this.addFlexiblePeriodToFilter(
                groupFilter,
                group.flexible_period.start,
                "gte"
            );
        }

        if (group.flexible_period.end.interval > 0 && group.isFlexibleDate) {
            this.addFlexiblePeriodToFilter(
                groupFilter,
                group.flexible_period.end,
                "lte"
            );
        }

        return groupFilter;
    }

    addCategoryAndCatalogData(
        groupFilter: Filter,
        groupFilterForDB: Filter,
        group: ConditionsGroup,
        ageMappings?: AgeMappings
    ) {
        this.addCategoryToFilter(groupFilter, groupFilterForDB, group);
        this.addCatalogDataToFilter(groupFilter, group, false, ageMappings);
        this.addCatalogDataToFilter(groupFilterForDB, group, true, ageMappings);
    }

    updateFilters(filter: Filter, filterForDB: Filter) {
        this.filter.filters_by_ids = filter;
        this.filter.filters_for_db = filterForDB;
        this.stringFilter = this.convertFilterToString();
    }

    addCatalogDataToFilter(groupFilter: Filter, group: any, withNames: boolean, ageMappings?: AgeMappings) {
        if (withNames) {
            this.addToFilter(groupFilter, group, "shop", "shop_pk", "shop_pk");
            this.addToFilter(groupFilter, group, "brand", "brand_name", "brand");
            this.addGenderToFilter(groupFilter, group, withNames);
            this.addAgeToFilter(groupFilter, group, withNames, ageMappings);
        } else {
            this.addToFilter(groupFilter, group, "shop", "id", "shop");
            this.addToFilter(groupFilter, group, "brand", "brand_pk", "brand");
            this.addGenderToFilter(groupFilter, group, withNames);
            this.addAgeToFilter(groupFilter, group, withNames, ageMappings);
        }
        this.addTotalToFilter(groupFilter, group);
    }

    addToFilter(
        groupFilter: Filter,
        obj: any,
        obj_type: string,
        attr: string,
        type: string
    ) {
        if (obj[obj_type].length > 0) {
            const values = obj[obj_type].map((elem: any) =>
                this.parseStrToInt(elem[attr])
            );
            groupFilter.operands.push({ type: type, value: values, attribute: "in" });
        }
    }

    addGenderToFilter(groupFilter: Filter, group: any, withNames: boolean) {
        if (group.gender && group.gender.length > 0) {
            const genderValues = withNames
                ? convertGenderValuesToNames(group.gender)
                : group.gender;

            groupFilter.operands.push({
                type: "gender",
                value: genderValues,
                attribute: "in"
            });
        }
    }

    addAgeToFilter(groupFilter: Filter, group: any, withNames: boolean, ageMappings?: AgeMappings) {
        if (group.age && group.age.length > 0) {
            let ageValues = group.age;

            if (withNames && ageMappings) {
                ageValues = group.age.map((id: number) => {
                    return ageMappings.idToType[id] ?? id.toString();
                });
            }

            groupFilter.operands.push({
                type: "age",
                value: ageValues,
                attribute: "in"
            });
        }
    }

    addTotalToFilter(groupFilter: Filter, group: any) {
        if (
            group.total != null &&
            typeof group.total === "number" &&
            Number.isFinite(group.total) &&
            group.total >= 0
        ) {
            groupFilter.operands.push({
                type: "total",
                value: group.total,
                attribute: "gte",
            });
        }
        if (
            group.maxTotal != null &&
            typeof group.maxTotal === "number" &&
            Number.isFinite(group.maxTotal) &&
            group.maxTotal >= 0
        ) {
            groupFilter.operands.push({
                type: "total",
                value: group.maxTotal,
                attribute: "lte",
            });
        }
    }

    addPeriodToFilter(groupFilter: Filter, date: Date, attribute: string) {
        groupFilter.operands.push({
            type: "date",
            value: formatDateToYYYYMMDD(date),
            attribute: attribute,
        });
    }

    addFlexiblePeriodToFilter(
        groupFilter: Filter,
        date: FlexiblePeriodItem,
        attribute: string
    ) {
        groupFilter.operands.push({
            type: "date",
            value: { interval: date.interval, unit: date.unit },
            attribute: attribute,
        });
    }

    getSegmentConditionsByFilter(filter: Filter = this.filter.filters_by_ids, ageMappings?: AgeMappings) {
        const operands = filter.operands;
        const conditionsGroups: ConditionsGroup[] = [];

        if (operands.length > 0 && "operands" in operands[0]) {
            operands.forEach((operand) => {
                if ("operands" in operand)
                    conditionsGroups.push(this.parseFilterGroup(operand.operands, ageMappings));
            });
        } else {
            conditionsGroups.push(this.parseFilterGroup(operands, ageMappings));
        }
        return conditionsGroups;
    }

    parseFilterGroup(operands: any, ageMappings?: AgeMappings) {
        const conditionsGroup: any = {
            category: [],
            brand: [],
            shop: [],
            gender: [],
            age: [],
            total: null,
            maxTotal: null,
            minPurchasesCount: null,
            maxPurchasesCount: null,
            period: {
                start: null,
                end: null,
            },
            flexible_period: {
                start: {
                    interval: 0,
                    unit: "DAY",
                },
                end: {
                    interval: 0,
                    unit: "DAY",
                },
            },
            isFixedDate: true,
            isFlexibleDate: false,
        };
        operands.forEach((operand: Operand) => {
            if (["category", "shop", "brand", "gender", "age"].includes(operand.type)) {
                if (operand.type === "gender") {
                    const genderValues = Array.isArray(operand.value) ? operand.value : [operand.value];
                    conditionsGroup[operand.type] = genderValues
                        .map((val: any) => {
                            if (typeof val === "string") {
                                if (val === GENDER_NAMES.MALE) return GENDER_VALUES.MALE;
                                if (val === GENDER_NAMES.FEMALE) return GENDER_VALUES.FEMALE;
                                const num = parseInt(val, 10);
                                return isNaN(num) ? 0 : num;
                            }
                            return typeof val === "number" ? val : parseInt(val, 10) || 0;
                        })
                        .filter((v: number) => v === GENDER_VALUES.MALE || v === GENDER_VALUES.FEMALE);
                } else {
                    conditionsGroup[operand.type] = operand.value;
                }
            } else if (operand.type === "date") {
                if (operand.attribute === "gte") {
                    if (typeof operand.value !== 'object') {
                        conditionsGroup.period.start = new Date(operand.value);
                        this.setDateType(conditionsGroup, true);
                    }
                    else {
                        conditionsGroup.flexible_period.start = { ...operand.value };
                        this.setDateType(conditionsGroup, false);
                    }
                } else {
                    if (typeof operand.value !== 'object')
                        conditionsGroup.period.end = new Date(operand.value);
                    else
                        conditionsGroup.flexible_period.end = { ...operand.value }
                }
            } else if (operand.type === "total") {
                const parsed = Number(operand.value);
                if (!Number.isFinite(parsed)) return;
                if (operand.attribute === "lte") {
                    conditionsGroup.maxTotal = parsed;
                } else {
                    conditionsGroup.total = parsed;
                }
            }
        });

        return conditionsGroup;
    }

    setDateType(conditionsGroup: any, isFixed: boolean) {
        conditionsGroup.isFixedDate = isFixed;
        conditionsGroup.isFlexibleDate = !isFixed;
    }

    convertFilterToString(filter: Filter = this.filter.filters_by_ids): string {
        const operands = filter.operands.filter(e => {
            if ("operands" in e) return true;
            if (!("value" in e)) return false;
            if (e.type === "date" || e.type === "total") return true;
            if (typeof e.value === "number") return Number.isFinite(e.value);
            return e.value.length > 0;
        });
        const parts = operands.map((operand) => {
            if ("operands" in operand) {
                return `(${this.convertFilterToString(operand)})`;
            } else {
                let values = "";
                if (Array.isArray(operand.value)) {
                    values = operand.value.join(",");
                } else if (typeof operand.value === "object") {
                    values = objectToString(operand.value);
                } else {
                    values = operand.value;
                }
                return `${operand.type}:${operand.attribute}:${values}`;
            }
        });

        const operator = filter.operator === "and" ? "&" : "|";
        return `(${parts.join(`${operator}`)})`;
    }

    convertStringToFilter(str: string): Filter {
        const operands = [];
        let operator: string | undefined = "";
        let parts: string[] = [];

        if (str.match(groupFilterRegex)) {
            parts = extractTopLevelBracketExpressions(str);
            operator = str[parts[0].length + 2];
            parts = parts.map((e) => {
                if (e[0] !== "(") return `(${e})`;
                return e;
            });
        } else {
            parts = str.slice(1, -1).split(/&|\|/);
            operator = parts.length > 1 ? str[parts[0].length + 1] : "&";
        }

        for (let i = 0; i < parts.length; i++) {
            operands.push(this.parseOperand(parts[i]));
        }

        operator = operators.get(operator);
        return { operands, operator };
    }

    parseOperand(part: string) {
        if (part[0] === "(") {
            return this.convertStringToFilter(part);
        } else {
            const [type, attribute, value] = part.split(":");
            let values: any;
            if (value.includes("[")) {
                values = parseStringToObject(value);
            }
            else if (value.includes(",") || attribute === "in") {
                values = value.split(",");
            } else if (type === "total") {
                values = parseFloat(value);
            } else {
                values = value
            }
            if (Array.isArray(values) && values[0] !== "") {
                values = values.map((e) => {
                    if (Number.isInteger(+e)) return parseInt(e);
                    return e;
                });
            }
            if (Array.isArray(values) && values[0] === "") {
                values = [];
            }
            return {
                type,
                attribute,
                value: values,
            };
        }
    }

    getOperandsByCategory(category: any) {
        const operands: (Operand | Filter)[] = [];
        Object.keys(category).forEach((key) => {
            const keys = key.split("-");
            if (keys[0] === "") {
                if (category[key].length)
                    operands.push({ type: "c1", value: category[key], attribute: "in" });
            } else {
                const ops = { operands: [] as any[], operator: "and" };
                let i = 0;
                for (i; i < keys.length; i++) {
                    if (keys[i].length)
                        ops.operands.push({
                            type: `c${i + 1}`,
                            value: [keys[i]],
                            attribute: "in",
                        });
                }
                if (category[key].length)
                    ops.operands.push({
                        type: `c${i + 1}`,
                        value: category[key],
                        attribute: "in",
                    });
                operands.push(ops);
            }
        });
        return operands;
    }

    addCategoryToFilter(groupFilter: Filter, groupFilterForDB: Filter, obj: any) {
        const values: number[] = [];
        const category: any = { "": [] as string[] };

        for (const key in obj.category) {
            const parentKeyParts = key.split("-");
            const catId = parentKeyParts.pop();
            const parentKey = parentKeyParts.join("-");

            if (
                catId !== "0" &&
                obj.category[key].checked &&
                !(obj.category[parentKey] && obj.category[parentKey].checked)
            ) {
                if (catId) {
                    values.push(parseInt(catId));
                    if (!(parentKeyParts.join("-") in category))
                        category[parentKeyParts.join("-")] = [];
                    category[parentKeyParts.join("-")].push(catId);
                }
            }
        }

        const operands = this.getOperandsByCategory(category);

        if (operands.length == 1) {
            groupFilterForDB.operands.push(operands[0]);
        } else if (operands.length > 1) {
            groupFilterForDB.operands.push({ operator: "or", operands: operands });
        }

        if (values.length)
            groupFilter.operands.push({
                type: "category",
                value: values,
                attribute: "in",
            });
    }

    parseStrToInt(e: string) {
        if (Number.isInteger(+e)) return parseInt(e);
        return e;
    }
}

export interface Operand {
    type: string;
    value: any;
    attribute: string;
}

export interface Filter {
    operands: Array<Filter | Operand>;
    operator?: string;
}

export interface Filters {
    filters_by_ids: Filter;
    filters_for_db?: Filter;
    version: string;
    min_purchases_count?: number | null;
    max_purchases_count?: number | null;
}

export interface SelectData {
    name: string;
    code: string;
}

export interface ConditionsGroup {
    period: Period;
    flexible_period: FlexiblePeriod;
    category: any[];
    brand: any[];
    shop: any[];
    gender: number[];
    age: number[];
    total: number | null;
    maxTotal: number | null;
    minPurchasesCount: number | null;
    maxPurchasesCount: number | null;
    isFixedDate: boolean,
    isFlexibleDate: boolean,
}

export interface DropdownData {
    name: string;
    value: string;
}

export interface Period {
    start: Date | null;
    end: Date | null;
}

export interface FlexiblePeriod {
    start: FlexiblePeriodItem;
    end: FlexiblePeriodItem;
}

export interface FlexiblePeriodItem {
    interval: number;
    unit: string;
}

interface Segment {
    name: string;
    type: DropdownData;
    conditionsGroups: ConditionsGroup[];
}
